/**
 * Microsoft Ads (Bing) Adapter
 * 
 * Adapts event data into Microsoft Ads UET format.
 * 
 * Documentation:
 * - https://help.ads.microsoft.com/#apex/ads/en/56684/2
 * - https://help.ads.microsoft.com/#apex/ads/en/60118/-1
 * 
 * @since 1.51.0
 */

(function (wpm, $, undefined) {

	class MicrosoftAdsAdapter {
		
		/**
		 * Adapt event data to Microsoft Ads format
		 * 
		 * @param {object} eventData - Event-specific data (product, order, etc.)
		 * @param {string} eventName - Standardized event name
		 * @param {object} context - Event context (user, page, etc.)
		 * @returns {object|null} Adapted data for Microsoft Ads or null if pixel not loaded
		 */
		adapt(eventData, eventName, context) {
			
			// Check if Microsoft Ads pixel is loaded
			if (!wpmDataLayer?.pixels?.bing?.loaded) {
				return null;
			}
			
			const eventMap = {
				'add_to_cart': () => this.adaptAddToCart(eventData),
				'view_item': () => this.adaptViewItem(eventData),
				'view_category': () => this.adaptViewCategory(eventData),
				'search': () => this.adaptSearch(eventData),
				'purchase': () => this.adaptPurchase(eventData),
				'everywhere_else': () => this.adaptEverywheereElse(eventData),
			};
			
			if (!eventMap[eventName]) {
				return null;
			}
			
			return eventMap[eventName]();
		}
		
		/**
		 * Adapt add_to_cart event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 */
		adaptAddToCart(eventData) {
			if (!eventData?.product) return null;
			
			const product = eventData.product;
			
			return {
				event_name: 'add_to_cart',
				event_data: {
					ecomm_pagetype: 'cart',
					ecomm_prodid: product.dyn_r_ids?.[wpmDataLayer.pixels.bing.dynamic_remarketing.id_type],
				}
			};
		}
		
		/**
		 * Adapt view_item event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 */
		adaptViewItem(eventData) {
			const data = {
				event_name: '',
				event_data: {}
			};
			
			if (eventData?.product) {
				data.event_data.ecomm_pagetype = 'product';
				data.event_data.ecomm_prodid = eventData.product.dyn_r_ids?.[wpmDataLayer.pixels.bing.dynamic_remarketing.id_type];
			}
			
			return data;
		}
		
		/**
		 * Adapt view_category event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 */
		adaptViewCategory(eventData) {
			const data = {
				event_name: '',
				event_data: {
					ecomm_pagetype: 'category',
				}
			};
			
			if (Object.keys(wpmDataLayer?.products || {}).length) {
				data.event_data.ecomm_prodid = wpm.getBingDataLayerProducts();
			}
			
			return data;
		}
		
		/**
		 * Adapt search event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 */
		adaptSearch(eventData) {
			const data = {
				event_name: '',
				event_data: {
					ecomm_query: wpm.getSearchTermFromUrl(),
					ecomm_pagetype: 'searchresults',
				}
			};
			
			if (Object.keys(wpmDataLayer?.products || {}).length) {
				data.event_data.ecomm_prodid = wpm.getBingDataLayerProducts();
			}
			
			return data;
		}
		
		/**
		 * Adapt purchase event
		 * https://help.ads.microsoft.com/#apex/ads/en/60118/-1
		 */
		adaptPurchase(eventData) {
			return {
				event_name: 'purchase',
				event_data: {
					ecomm_pagetype: 'purchase',
					ecomm_prodid: wpm.bing_purchase_ecomm_prodids(),
					revenue_value: wpmDataLayer.order.value.marketing,
					currency: wpmDataLayer.order.currency,
					items: wpm.bingPurchaseItems(),
				}
			};
		}
		
		/**
		 * Adapt everywhere_else event (generic page views)
		 * The ecomm_pagetype parameter identifies the type of page the user has visited.
		 * Valid options: home, searchresults, category, product, cart, purchase, other.
		 */
		adaptEverywheereElse(eventData) {
			const data = {
				event_name: '',
				event_data: {
					ecomm_pagetype: wpmDataLayer?.shop?.page_type === 'front_page' ? 'home' : 'other',
				}
			};
			
			if (Object.keys(wpmDataLayer?.products || {}).length) {
				data.event_data.ecomm_prodid = wpm.getBingDataLayerProducts();
			}
			
			return data;
		}
	}
	
	// Register the adapter
	wpm.registerAdapter('bing', new MicrosoftAdsAdapter(), {
		supportsS2S: false,
		supportsEventDeduplication: false,
		isActive: () => wpmDataLayer?.pixels?.bing?.uet_tag_id ? true : false
	});
	
}(window.wpm = window.wpm || {}, jQuery));
